import React, { useState, useRef, useEffect } from "react";
import PT from "../i18n/platformTranslations";
import { PLATFORM_SEED } from "../state/platformState";
import { anchorDerivedColors } from "../utils/color";
import { buildDarkAccentCss, buildLightAccentCss } from "../utils/themeAccent";
import { usePlatformThemeMode, tc, themeColors, featureIcon, showcaseVisual } from "./themeUtils";
import HeroDashboardSVG from "./HeroDashboardSVG";
import { SplashContent } from "../shared/SplashScreen";
import CommandNav from "./CommandNav";
import StoreBadges from "./StoreBadges";
import InstallAppButton from "./InstallAppButton";

export default
function PlatformLanding({platform, onSignup, onLogin, onAdmin, onLegal, lang, setLang}){
  const t=PT[lang];
  const isRTL=lang==="ar";
  /* Default accent switched from gold (#C9A84C) to the handoff's violet primary
     (#7c3aed). The violet→cyan gradient itself is used sparingly (≤2 per
     viewport) in the high-impact surfaces: logo tile, SOON pill, hero accent
     word. Platform admins can still override via platform.platformAccent. */
  const accent=platform.platformAccent||"#7c3aed";
  const theme=usePlatformThemeMode(platform);
  const C=themeColors(theme);
  const heroBadge   = platform.platformHeroBadge   || (isRTL?"الوصول المبكر":"Now in Early Access");
  const heroLine1   = platform.platformHeroLine1    || (isRTL?"منزلك.":"Your Household.");
  const heroLine2   = platform.platformHeroLine2    || (isRTL?"تحت السيطرة.":"Fully Commanded.");
  const ctaText     = platform.platformCtaText      || (isRTL?"ابدأ الآن":"Get Started");
  const secondaryBtn= platform.platformHeroSecondaryBtn || (isRTL?"شاهد كيف يعمل":"See How It Works");
  const signInText  = platform.platformSignInText   || t.signIn;
  const signUpText  = platform.platformSignUpText   || (isRTL?"ابدأ":"Get Started");
  const tog=Object.assign({badge:true,line1:true,line2:true,tagline:true,cta:true,signin:true,signup:true,secondaryCta:true,heroImage:true,features:true,showcases:true,pricing:true,finalCta:true,navLinks:true},platform.platformLandingToggles||{});
  const DFS={badge:11,line1:52,tagline:17,cta:15,signin:13,signup:13};
  const fsz=Object.assign({...DFS},platform.platformLandingFontSizes||{});
  const accentCss=theme==="dark"?buildDarkAccentCss(accent):buildLightAccentCss(accent);
  /* Landing page uses San Francisco (system stack) for EN. Tajawal is retained
     for Arabic since SF's Arabic shaping differs from a dedicated Arabic face.
     Admin-configured platformHeadingFont / platformBodyFont are intentionally
     ignored on the landing per design direction. */
  const SF="-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Helvetica Neue',Helvetica,Arial,sans-serif";
  const hdFont=isRTL?"'Tajawal',"+SF:SF;
  const bdFont=isRTL?"'Tajawal',"+SF:SF;
  const hdWeight=600;
  const hdStyle="normal";
  const features=platform.platformFeatures||PLATFORM_SEED.platformFeatures;
  const showcases=platform.platformShowcases||PLATFORM_SEED.platformShowcases;
  const pkgs=platform.platformPackages||[];
  const allowedCycles=platform.allowedBillingCycles||"both";
  const [billingCycle,setBillingCycle]=useState(allowedCycles==="yearly"?"yearly":"monthly");
  const scrollTo=(id)=>{const el=document.getElementById(id);if(el)el.scrollIntoView({behavior:"smooth"});};
  const navLinks=platform.platformNavLinks&&platform.platformNavLinks.length>0?platform.platformNavLinks:[];
  const [showModules,setShowModules]=useState(null);
  const [showVideo,setShowVideo]=useState(false);

  /* ── Mobile detection ── */
  const [isMobile,setIsMobile]=useState(()=>typeof window!=="undefined"&&window.innerWidth<=768);
  useEffect(()=>{
    const h=()=>setIsMobile(window.innerWidth<=768);
    window.addEventListener("resize",h);
    return()=>window.removeEventListener("resize",h);
  },[]);

  /* ── Mobile swipe card state ── */
  const pmlRef=useRef(null);
  const pmlHeroRef=useRef(null);
  const [pmlIdx,setPmlIdx]=useState(0);
  const [pmlCardH,setPmlCardH]=useState(0);
  const pmlTimer=useRef(null);
  /* Measure the hero card to set uniform height for all cards — re-measure on lang change */
  useEffect(()=>{
    if(!isMobile||!pmlHeroRef.current)return;
    const measure=()=>{
      const h=pmlHeroRef.current?.offsetHeight;
      if(h&&h>100)setPmlCardH(h);
    };
    /* Reset and re-measure after a brief delay for font/layout to settle */
    setPmlCardH(0);
    const t=setTimeout(measure,80);
    if(document.fonts&&document.fonts.ready)document.fonts.ready.then(measure);
    return()=>clearTimeout(t);
  },[isMobile,platform._hydrated,lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const pmlOnScroll=()=>{
    if(pmlTimer.current)clearTimeout(pmlTimer.current);
    pmlTimer.current=setTimeout(()=>{
      if(!pmlRef.current)return;
      const idx=Math.round(pmlRef.current.scrollLeft/window.innerWidth);
      setPmlIdx(idx);
    },60);
  };
  const pmlGoTo=(idx)=>{
    if(!pmlRef.current)return;
    pmlRef.current.scrollTo({left:idx*window.innerWidth,behavior:"smooth"});
    setPmlIdx(idx);
  };

  /* ── Build mobile slides array ── */
  const pmlSlides=[];
  pmlSlides.push("hero");
  if(tog.features&&features.length>0)pmlSlides.push("features");
  if(tog.showcases&&showcases.length>0)showcases.forEach((_,i)=>pmlSlides.push("showcase-"+i));
  pmlSlides.push("cta");

  /* ── Mobile accent: use workspace anchor colour — replicate exact workspace colour logic.
     Default swapped from gold (#C9A84C) to violet (#7c3aed) so the derived tonal palette
     lives in the same family as the desktop iridescent gradient. Admin override still wins. */
  const mobileAccent=platform.wsDefaultAnchorColor||platform.platformAccent||"#7c3aed";
  const {primary:mPrimary,palette:mPalette}=anchorDerivedColors(mobileAccent);
  const at=(opacity)=>mobileAccent+Math.round(opacity*255).toString(16).padStart(2,"0");
  /* Exact workspace mapping: ink, muted, subtle from tonal palette */
  const accentInk   =theme==="dark"?mPalette[10]:mPrimary;        /* --ink: heading text */
  const accentBody  =theme==="dark"?mPalette[8]:mPalette[4];      /* body/feature titles */
  const accentMuted =theme==="dark"?mPalette[6]:mPalette[5];      /* descriptions, subtitles */
  const accentSubtle=theme==="dark"?mPalette[4]:mPalette[7];      /* hints, footer text */
  const accentText  =mobileAccent;                                 /* accent itself (buttons, badges) */
  const accentBg    =theme==="dark"?at(.08):at(.06);
  const accentBd    =theme==="dark"?at(.18):at(.14);
  const accentGrad  =`linear-gradient(135deg,${theme==="dark"?mPalette[5]:mPalette[3]},${theme==="dark"?mPalette[4]:mPalette[2]})`;
  const accentBtnText=theme==="dark"?"#fff":"#fff";
  /* Mobile system font — native iOS feel */
  const sysFont=isRTL?"'Tajawal',-apple-system,sans-serif":"-apple-system,'SF Pro Display','SF Pro Text','Helvetica Neue',sans-serif";

  /* ══════ MOBILE LANDING (swipe cards) ══════ */
  if(isMobile){
    /* Wait for real platform data before rendering — show branded splash */
    if(!platform._hydrated){
      return <div className="splash-screen"><SplashContent/></div>;
    }
    const mobileAccentCss=theme==="dark"?buildDarkAccentCss(mobileAccent):buildLightAccentCss(mobileAccent);
    return(
      <div className={`cmd-landing ${theme==="dark"?"p-dark":"p-light"}${isRTL?" rtl":""} pml-wrap`}>
        <style>{`\nbody{background:${theme==="dark"?"#08080f":"#FAFAF9"};overflow:hidden;margin:0;}`}</style>
        {mobileAccentCss&&<style>{mobileAccentCss}</style>}
        <div className={tc(theme,"p-dark-blob")}/>
        <div className="p-hero-grid" style={{position:"fixed"}}/>

        {/* ── Swipe viewport (no top nav — app-native feel) ── */}
        <div className="pml-viewport" ref={pmlRef} onScroll={pmlOnScroll}>

          {pmlSlides.map((slide,si)=>{
            /* ── HERO SLIDE ── */
            if(slide==="hero") return(
              <div key="hero" className="pml-slide">
                <div ref={pmlHeroRef} className="pml-glass" style={{textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                  {/* App logo — wrapped in the iridescent gradient tile, echoing the desktop nav */}
                  <div style={{marginBottom:20,width:80,height:80,borderRadius:18,background:"var(--accent-grad)",display:"grid",placeItems:"center",overflow:"hidden",boxShadow:"0 8px 28px -6px color-mix(in srgb, var(--accent-a) 50%, transparent)"}}>
                    <img src={platform.platformLogo||"/icon.svg"} style={{width:"100%",height:"100%",objectFit:"contain",filter:"brightness(0) invert(1)"}} alt={platform.platformName||"Command"}/>
                  </div>
                  {tog.badge&&<div className="pml-hero-badge" style={{background:accentBg,border:`1px solid ${accentBd}`,color:accentText,marginBottom:16}}>
                    <span style={{width:7,height:7,borderRadius:"50%",background:mobileAccent}}/>
                    {heroBadge}
                  </div>}
                  <h1 className="pml-hero-title" style={{fontFamily:sysFont,color:accentInk}}>
                    {tog.line1&&heroLine1}{tog.line1&&tog.line2&&<br/>}{tog.line2&&<span className="grad-text">{heroLine2}</span>}
                  </h1>
                  {tog.tagline&&<p className="pml-hero-sub" style={{color:accentMuted}}>
                    {platform.platformTagline||"The operating system for modern living. Centralize home management, staff, expenses, and automation in one premium control system."}
                  </p>}
                  {/* Two CTAs: Sign Up + Sign In */}
                  <div style={{display:"flex",flexDirection:"column",gap:10,width:"100%",maxWidth:280}}>
                    <button className="pml-btn" style={{background:"var(--accent-grad)",color:"#fff",border:"none",borderRadius:14,fontSize:17,fontWeight:600,padding:"15px 28px",cursor:"pointer",letterSpacing:"-.01em",width:"100%",boxShadow:"0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)"}} onClick={onSignup}>{signUpText||ctaText}</button>
                    <button className="pml-btn" style={{background:theme==="dark"?"rgba(255,255,255,.08)":"rgba(0,0,0,.04)",color:accentBody,border:`1px solid ${accentBd}`,borderRadius:14,fontSize:17,fontWeight:600,padding:"15px 28px",cursor:"pointer",width:"100%",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)"}} onClick={onLogin}>{signInText}</button>
                    {/* Instant-install PWA button — hides if already standalone; falls back to an instructions modal where the native prompt is unavailable (iOS Safari). */}
                    <InstallAppButton lang={lang} variant="ghost" />
                  </div>
                  {/* Native store badges — SOON pill overlay, compact for mobile card */}
                  <div style={{marginTop:22,display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
                    <div style={{fontSize:10,fontWeight:600,letterSpacing:".14em",textTransform:"uppercase",color:accentSubtle}}>
                      {isRTL?"قريباً على":"Coming soon to"}
                    </div>
                    <StoreBadges
                      compact
                      iosLabels={isRTL?{tiny:"حمّل من",name:"App Store",soon:"قريباً",aria:"App Store — قريباً"}:undefined}
                      androidLabels={isRTL?{tiny:"احصل عليه من",name:"Google Play",soon:"قريباً",aria:"Google Play — قريباً"}:undefined}
                    />
                  </div>
                  {pmlSlides.length>1&&<div className="pml-swipe-hint" style={{color:accentSubtle,marginTop:20}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    {isRTL?"اسحب لليسار":"Swipe to explore"}
                  </div>}
                </div>
              </div>
            );

            /* ── FEATURES SLIDE ── */
            if(slide==="features") return(
              <div key="features" className="pml-slide">
                <div className="pml-glass" style={{display:"flex",flexDirection:"column",...(pmlCardH?{height:pmlCardH}:{})}}>
                  <div style={{marginBottom:12,flexShrink:0}}>
                    <h2 style={{fontFamily:sysFont,fontSize:20,fontWeight:700,color:accentInk,marginBottom:6}}>
                      {platform.platformFeaturesHeading||(isRTL?<>مصمم <span className="grad-text">للنتائج</span></>:<>Built for <span className="grad-text">outcomes</span></>)}
                    </h2>
                    <p style={{fontSize:13,color:accentMuted}}>{platform.platformFeaturesSub||(isRTL?"يمنحك كومند التحكم الكامل":"Complete control of your household")}</p>
                  </div>
                  <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",minHeight:0}}>
                    {features.map((f,i)=>(
                      <div key={i} className="pml-feat-item">
                        <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg, color-mix(in srgb, var(--accent-a) 18%, transparent), color-mix(in srgb, var(--accent-b) 10%, transparent))",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,border:"1px solid color-mix(in srgb, var(--accent-a) 18%, transparent)"}}>{featureIcon(f.icon,accentText)}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:14,fontWeight:600,color:accentBody,marginBottom:2}}>{f.title}</div>
                          <div style={{fontSize:12,color:accentMuted,lineHeight:1.5}}>{f.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );

            /* ── SHOWCASE SLIDES ── */
            if(slide.startsWith("showcase-")){
              const sIdx=parseInt(slide.split("-")[1]);
              const s=showcases[sIdx];
              if(!s)return null;
              return(
                <div key={slide} className="pml-slide">
                  <div className="pml-glass" style={{display:"flex",flexDirection:"column",justifyContent:"center",...(pmlCardH?{height:pmlCardH}:{})}}>
                    <div style={{paddingBottom:14,flexShrink:0}}>
                      <h3 style={{fontFamily:sysFont,fontSize:18,fontWeight:700,color:accentInk,marginBottom:6}}>{s.title}</h3>
                      <p style={{fontSize:14,color:accentMuted,lineHeight:1.6}}>{s.desc}</p>
                    </div>
                    <div className="pml-showcase-visual" style={{border:`1px solid ${accentBd}`,borderRadius:14,flexShrink:0}}>
                      {showcaseVisual(sIdx,theme,accentText,C,s.title)}
                    </div>
                  </div>
                </div>
              );
            }

            /* ── SIGN UP CTA SLIDE (final card) ── */
            if(slide==="cta") return(
              <div key="cta" className="pml-slide">
                <div className="pml-glass" style={{textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",...(pmlCardH?{height:pmlCardH}:{})}}>
                  {/* Logo — gradient tile */}
                  <div style={{marginBottom:18,width:72,height:72,borderRadius:16,background:"var(--accent-grad)",display:"grid",placeItems:"center",overflow:"hidden",boxShadow:"0 8px 24px -6px color-mix(in srgb, var(--accent-a) 50%, transparent)"}}>
                    <img src={platform.platformLogo||"/icon.svg"} style={{width:"100%",height:"100%",objectFit:"contain",filter:"brightness(0) invert(1)"}} alt={platform.platformName||"Command"}/>
                  </div>
                  <h2 style={{fontFamily:sysFont,fontSize:24,fontWeight:700,color:accentInk,marginBottom:10}}>
                    {platform.platformFinalCtaHeading||(isRTL?<>تحكم <span className="grad-text">بمنزلك</span></>:<>Take <span className="grad-text">Control</span></>)}
                  </h2>
                  <p style={{fontSize:13,color:accentMuted,marginBottom:28,lineHeight:1.6,maxWidth:300}}>
                    {platform.platformFinalCtaSub||(isRTL?"انضم لمئات الأسر التي تستخدم كومند":"Join hundreds of households already using Command.")}
                  </p>
                  <div style={{display:"flex",flexDirection:"column",gap:10,width:"100%",maxWidth:280}}>
                    <button className="pml-btn" style={{background:"var(--accent-grad)",color:"#fff",border:"none",borderRadius:14,fontSize:16,fontWeight:600,padding:"15px 36px",cursor:"pointer",width:"100%",boxShadow:"0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)"}} onClick={onSignup}>
                      {signUpText||(isRTL?"سجّل":"Sign Up")}
                    </button>
                    <button className="pml-btn" style={{background:theme==="dark"?"rgba(255,255,255,.08)":"rgba(0,0,0,.04)",color:accentBody,border:`1px solid ${accentBd}`,borderRadius:14,fontSize:16,fontWeight:600,padding:"15px 28px",cursor:"pointer",width:"100%",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)"}} onClick={onLogin}>{signInText}</button>
                  </div>
                  <div style={{display:"flex",gap:16,flexWrap:"wrap",justifyContent:"center",marginTop:24}}>
                    {[
                      {k:"privacyPolicy",en:"Privacy",ar:"الخصوصية"},
                      {k:"termsOfService",en:"Terms",ar:"الشروط"},
                      {k:"userDataDeletion",en:"Data Deletion",ar:"حذف البيانات"},
                      {k:"supportPage",en:"Support",ar:"الدعم"},
                    ].map(p=>(
                      <button key={p.k} onClick={()=>onLegal&&onLegal(p.k)}
                        style={{background:"none",border:"none",color:accentSubtle,fontSize:10,cursor:"pointer",textDecoration:"underline",textUnderlineOffset:3}}>{isRTL?p.ar:p.en}</button>
                    ))}
                  </div>
                  <div style={{marginTop:10,fontSize:10,color:accentSubtle}}>© {new Date().getFullYear()} {(platform.legalPages||{}).companyName||"Command Platform"}</div>
                </div>
              </div>
            );

            return null;
          })}
        </div>

        {/* ── Page indicator + language toggle — single row ── */}
        <div style={{position:"fixed",bottom:24,left:0,right:0,display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"0 24px",zIndex:10}}>
          <div style={{fontSize:12,fontWeight:600,color:accentMuted,letterSpacing:".02em",flexShrink:0}}>
            <span style={{color:accentText}}>{pmlIdx+1}</span>
            <span style={{opacity:.4}}>{" / "}{pmlSlides.length}</span>
          </div>
          <div style={{display:"flex",gap:3,flex:1,maxWidth:200}}>
            {pmlSlides.map((_,i)=>(
              <button key={i} onClick={()=>pmlGoTo(i)}
                style={{flex:1,height:3,borderRadius:2,border:"none",padding:0,cursor:"pointer",
                  background:i<=pmlIdx?"var(--accent-grad)":(theme==="dark"?"rgba(255,255,255,.12)":"rgba(0,0,0,.08)"),
                  boxShadow:i===pmlIdx?"0 1px 6px -2px color-mix(in srgb, var(--accent-a) 45%, transparent)":"none",
                  opacity:i===pmlIdx?1:i<pmlIdx?.5:.3,
                  transition:"all .3s cubic-bezier(.2,.9,.3,1)"}}/>
            ))}
          </div>
          <button className="pml-btn" onClick={()=>setLang(lang==="en"?"ar":"en")}
            style={{display:"flex",alignItems:"center",justifyContent:"center",background:theme==="dark"?"rgba(255,255,255,.08)":"rgba(0,0,0,.04)",
              border:`1px solid ${theme==="dark"?"rgba(255,255,255,.1)":"rgba(0,0,0,.06)"}`,
              borderRadius:20,width:30,height:30,padding:0,cursor:"pointer",flexShrink:0,
              backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",transition:"all .2s"}}>
            <span style={{fontSize:16,lineHeight:1}}>{lang==="en"?"\ud83c\uddf8\ud83c\udde6":"\ud83c\uddec\ud83c\udde7"}</span>
          </button>
        </div>
      </div>
    );
  }

  /* ══════ DESKTOP LANDING (original scroll) ══════ */
  return(
    <div className={`cmd-landing ${theme==="dark"?"p-dark":"p-light"}${isRTL?" rtl":""}`}
      style={{minHeight:"100vh",fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":bdFont}}>
      <style>{`\nbody{background:${theme==="dark"?"#08080f":"#FAFAF9"};overflow-x:hidden;}`}</style>
      {accentCss&&<style>{accentCss}</style>}
      <div className={tc(theme,"p-dark-blob")}/>

      {/* ── Nav (ported from handoff: gradient logo tile + center pill + actions) ── */}
      <CommandNav
        logoSrc={platform.platformLogo||"/icon.svg"}
        logoAlt={platform.platformName||"Command"}
        navLinks={tog.navLinks?navLinks:[]}
        showNavLinks={tog.navLinks}
        lang={lang}
        setLang={setLang}
        onLogoClick={()=>window.scrollTo({top:0,behavior:"smooth"})}
        onLogin={onLogin}
        onSignup={onSignup}
        signInText={signInText}
        signUpText={signUpText}
        showSignIn={!!tog.signin}
        showSignUp={!!tog.signup}
      />

      {/* ── Hero grid background ── */}
      <div className="p-hero-grid"/>

      {/* ── Hero Section ── */}
      <section style={{maxWidth:900,margin:"0 auto",padding:"100px 24px 60px",textAlign:"center",position:"relative",zIndex:1}}>
        {tog.badge&&<div className={tc(theme,"p-hero-tag-dark")} style={{marginBottom:24,fontSize:fsz.badge}}>
          <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:accent,marginInlineEnd:8,verticalAlign:"middle"}}/>
          {heroBadge}
        </div>}
        <h1 className={tc(theme,"p-hero-title-dark")} style={{fontSize:fsz.line1,marginBottom:22,lineHeight:1.1,fontFamily:hdFont,fontWeight:hdWeight,fontStyle:hdStyle,letterSpacing:"-0.02em"}}>
          {tog.line1&&heroLine1}{tog.line1&&tog.line2&&" "}{tog.line2&&<span style={{background:"var(--accent-grad)",WebkitBackgroundClip:"text",backgroundClip:"text",WebkitTextFillColor:"transparent",color:"transparent"}}>{heroLine2}</span>}
        </h1>
        {tog.tagline&&<p className={tc(theme,"p-hero-sub-dark")} style={{fontSize:fsz.tagline,marginBottom:40,maxWidth:640,marginInlineStart:"auto",marginInlineEnd:"auto"}}>
          {platform.platformTagline||"The operating system for modern living. Centralize home management, staff, expenses, and automation in one premium control system."}
        </p>}
        <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginBottom:28}}>
          {tog.cta&&<button className={tc(theme,"p-btn-accent-dark")} style={{fontSize:fsz.cta,padding:"14px 32px",borderRadius:10}} onClick={onSignup}>{ctaText}</button>}
          {tog.secondaryCta&&<button className={tc(theme,"p-btn-ghost-dark")} style={{padding:"14px 28px",borderRadius:10,fontSize:14}} onClick={()=>setShowVideo(true)}>
            <span style={{marginInlineEnd:6}}>&#9654;</span>{secondaryBtn}
          </button>}
        </div>
        {/* ── Store badges (ported from handoff: SOON pill overlay) ── */}
        <div style={{marginBottom:60}}>
          <StoreBadges
            iosLabels={isRTL?{tiny:"حمّل من",name:"App Store",soon:"قريباً",aria:"App Store — قريباً"}:undefined}
            androidLabels={isRTL?{tiny:"احصل عليه من",name:"Google Play",soon:"قريباً",aria:"Google Play — قريباً"}:undefined}
          />
        </div>
        {tog.heroImage&&(()=>{
          const heroMode=platform.platformHeroBuiltIn||"dashboard";
          const customImg=theme==="dark"?(platform.platformHeroImageDark||platform.platformHeroImage):platform.platformHeroImage;
          if(heroMode==="none")return null;
          return(
            <div style={{borderRadius:16,overflow:"hidden",border:`1px solid ${theme==="dark"?"rgba(255,255,255,.1)":"#E8E5E0"}`,boxShadow:theme==="dark"?"0 20px 60px rgba(0,0,0,.5)":"0 20px 60px rgba(0,0,0,.12)"}}>
              {heroMode==="custom"&&customImg?<img src={customImg} style={{width:"100%",display:"block"}} alt="Product preview"/>
              :<HeroDashboardSVG theme={theme} accent={accent}/>}
            </div>
          );
        })()}
      </section>

      {/* ── Features Section ── */}
      {tog.features&&features.length>0&&(
        <section id="features" style={{maxWidth:1000,margin:"0 auto",padding:"80px 24px",position:"relative",zIndex:1}}>
          <div style={{textAlign:"center",marginBottom:56}}>
            <h2 style={{fontFamily:hdFont,fontSize:34,fontWeight:hdWeight,fontStyle:hdStyle,color:C.text,marginBottom:12}}>
              {platform.platformFeaturesHeading||(isRTL?<>مصمم <span className="grad-text">للنتائج</span>، لا المميزات</>:<>Built for <span className="grad-text">outcomes</span>, not features</>)}
            </h2>
            <p style={{fontSize:15,color:C.muted,maxWidth:520,margin:"0 auto"}}>{platform.platformFeaturesSub||(isRTL?"يمنحك كومند التحكم الكامل بمنزلك عبر أربع ركائز أساسية.":"Command gives you complete control of your household with four core pillars.")}</p>
          </div>
          <div className="p-feat-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:0,border:`1px solid ${theme==="dark"?"rgba(255,255,255,.08)":"#E8E5E0"}`,borderRadius:16,overflow:"hidden"}}>
            {features.map((f,i)=>(
              <div key={i} style={{padding:"28px 24px",borderBottom:i<features.length-1?`1px solid ${theme==="dark"?"rgba(255,255,255,.08)":"#E8E5E0"}`:"none",display:"flex",gap:16,alignItems:"flex-start"}}>
                <div style={{width:44,height:44,borderRadius:12,background:"linear-gradient(135deg, color-mix(in srgb, var(--accent-a) 18%, transparent), color-mix(in srgb, var(--accent-b) 10%, transparent))",border:"1px solid color-mix(in srgb, var(--accent-a) 18%, transparent)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{featureIcon(f.icon,accent)}</div>
                <div>
                  <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:6}}>{f.title}</div>
                  <div style={{fontSize:13,color:C.muted,lineHeight:1.65}}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Showcase Sections (alternating layout) ── */}
      {tog.showcases&&showcases.length>0&&(
        <section style={{maxWidth:1000,margin:"0 auto",padding:"40px 24px 80px",position:"relative",zIndex:1}}>
          {showcases.map((s,i)=>(
            <div key={i} className={`p-showcase-row${i%2===1&&!isRTL?" reverse":""}`}>
              <div style={{direction:isRTL?"rtl":"ltr"}}>
                <h3 style={{fontFamily:hdFont,fontSize:26,fontWeight:hdWeight,fontStyle:hdStyle,color:C.text,marginBottom:12}}>{s.title}</h3>
                <p style={{fontSize:14,color:C.muted,lineHeight:1.7}}>{s.desc}</p>
              </div>
              <div style={{direction:isRTL?"rtl":"ltr"}}>{showcaseVisual(i,theme,accent,C,s.title)}</div>
            </div>
          ))}
        </section>
      )}

      {/* ── Pricing Section ── */}
      {tog.pricing&&pkgs.length>0&&(
        <section id="pricing" style={{maxWidth:1000,margin:"0 auto",padding:"80px 24px",position:"relative",zIndex:1}}>
          <div style={{textAlign:"center",marginBottom:40}}>
            <h2 style={{fontFamily:hdFont,fontSize:34,fontWeight:hdWeight,fontStyle:hdStyle,color:C.text,marginBottom:12}}>{platform.platformPricingHeading||(isRTL?<>أسعار <span className="grad-text">بسيطة</span> وشفافة</>:<>Simple, <span className="grad-text">transparent</span> pricing</>)}</h2>
            <p style={{fontSize:15,color:C.muted,marginBottom:24}}>{platform.platformPricingSub||(isRTL?"ابدأ مجاناً. توسّع عندما تكون جاهزاً.":"Start free. Scale when you're ready.")}</p>
            {(platform.globalTrialDays||0)>0&&(()=>{
              const d=platform.globalTrialDays;
              const trialLabel=d>=30?`${Math.round(d/30)}-month`:`${d}-day`;
              const trialLabelAr=d>=30?`${Math.round(d/30)} ${Math.round(d/30)===1?"شهر":"أشهر"}`:`${d} ${d===1?"يوم":"أيام"}`;
              return(
                <div style={{display:"inline-flex",alignItems:"center",gap:12,flexWrap:"wrap",justifyContent:"center",padding:"10px 18px",borderRadius:999,border:"1px solid color-mix(in srgb, var(--accent-a) 24%, transparent)",background:"color-mix(in srgb, var(--accent-a) 8%, transparent)",marginBottom:24,fontSize:13,fontWeight:500,color:C.text}}>
                  <span className="grad-text" style={{fontWeight:700}}>
                    {isRTL?`تجربة مجانية ${trialLabelAr}`:`${trialLabel} free trial`}
                  </span>
                  <span style={{color:C.muted}}>·</span>
                  <span>{isRTL?"لا حاجة لبيانات الدفع":"No credit card required"}</span>
                  <span style={{color:C.muted}}>·</span>
                  <span>{isRTL?"ألغِ في أي وقت":"Cancel anytime"}</span>
                </div>
              );
            })()}
            {allowedCycles==="both"&&pkgs.some(p=>(p.priceMonthly||0)>0||(p.priceYearly||0)>0)&&(
              <div style={{display:"inline-flex",background:theme==="dark"?"rgba(255,255,255,.06)":"#F5F4F2",borderRadius:30,padding:3,gap:2}}>
                {["monthly","yearly"].map(c=>(
                  <button key={c} onClick={()=>setBillingCycle(c)}
                    style={{padding:"8px 22px",borderRadius:26,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,transition:"all .15s",
                      background:billingCycle===c?"var(--accent-grad)":"transparent",
                      color:billingCycle===c?"#fff":C.muted}}>
                    {c==="monthly"?(isRTL?"شهري":"Monthly"):(isRTL?"سنوي":"Annual")}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-pkg-grid" style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(pkgs.length,3)},1fr)`,gap:20}}>
            {pkgs.map((pkg,pkgIdx)=>{
              const priceVal=billingCycle==="yearly"?(pkg.priceYearly||0):(pkg.priceMonthly||0);
              const hasPricing=(pkg.priceMonthly||0)>0||(pkg.priceYearly||0)>0;
              const moduleList=pkg.modules||[];
              const curr=pkg.currency||"SAR";
              const sym={SAR:"SAR",USD:"$",EUR:"\u20AC",GBP:"\u00A3",AED:"AED",KWD:"KWD",QAR:"QAR",BHD:"BHD",OMR:"OMR"}[curr]||curr;
              const priceDisplay=priceVal===0?(isRTL?"مجاني":"Free"):(["USD","EUR","GBP"].includes(curr)?`${sym}${priceVal}`:`${priceVal} ${sym}`);
              /* Incremental module display: find previous package's modules */
              const prevPkg=pkgIdx>0?pkgs[pkgIdx-1]:null;
              const prevModules=new Set(prevPkg?.modules||[]);
              const additionalModules=prevPkg?moduleList.filter(m=>!prevModules.has(m)):moduleList;
              const isLocked=pkg.showModules===false||!!pkg.inviteOnly;
              const mn=(platform.platformModuleNames||{});
              return(
                <div key={pkg.id} className={pkg.highlight?"cmd-pkg-highlight":undefined} style={{background:theme==="dark"?"rgba(255,255,255,.03)":"white",padding:28,position:"relative",display:"flex",flexDirection:"column",borderRadius:14,border:pkg.highlight?undefined:`1px solid ${theme==="dark"?"rgba(255,255,255,.1)":"#E8E5E0"}`,overflow:"visible"}}>
                  {pkg.highlight&&<div className="cmd-pkg-badge" style={{position:"absolute",top:-11,left:"50%",transform:"translateX(-50%)",fontSize:10,fontWeight:700,padding:"2px 14px",borderRadius:20,whiteSpace:"nowrap",zIndex:2,letterSpacing:"0.02em"}}>{isRTL?"الأكثر شيوعاً":"Most Popular"}</div>}
                  {/* Name */}
                  <div style={{fontSize:18,fontWeight:700,color:C.text,marginBottom:4,minHeight:24}}>{pkg.name}</div>
                  {/* Description — fixed height */}
                  <div style={{fontSize:13,color:C.muted,marginBottom:20,height:80,lineHeight:1.5,overflow:"hidden"}}>{pkg.description}</div>
                  {/* Price — during the trial promo, the monthly rate is
                      struck through and "X months FREE" is the big number
                      on the card so users see the headline offer first. */}
                  {(() => {
                    const hasTrial = hasPricing && priceVal>0 && (platform.globalTrialDays||0)>0;
                    const d = platform.globalTrialDays||0;
                    const trialLabel = hasTrial
                      ? (d>=30
                          ? (isRTL ? `${Math.round(d/30)} ${Math.round(d/30)===1?"شهر":"أشهر"} مجاناً` : `${Math.round(d/30)} month${Math.round(d/30)===1?"":"s"} FREE`)
                          : (isRTL ? `${d} ${d===1?"يوم":"أيام"} مجاناً` : `${d} day${d===1?"":"s"} FREE`))
                      : "";
                    return(
                      <div style={{marginBottom:12}}>
                        {hasTrial ? (
                          <>
                            <div className="grad-text" style={{fontSize:30,fontWeight:800,letterSpacing:"-0.02em",lineHeight:1.1,marginBottom:4}}>{trialLabel}</div>
                            <div style={{display:"flex",alignItems:"baseline",gap:6,fontSize:13,color:C.muted}}>
                              <span>{isRTL?"ثم":"then"}</span>
                              <span style={{textDecoration:"line-through",fontFamily:"'JetBrains Mono',monospace",opacity:0.7}}>{priceDisplay}</span>
                              <span>/{billingCycle==="yearly"?(isRTL?"سنة":"yr"):(isRTL?"شهر":"mo")}</span>
                            </div>
                          </>
                        ) : (
                          <div style={{height:48,display:"flex",alignItems:"baseline",gap:4}}>
                            <span style={{fontSize:32,fontWeight:700,color:C.text,fontFamily:"'JetBrains Mono',monospace"}}>{hasPricing?priceDisplay:""}</span>
                            {hasPricing&&priceVal>0&&<span style={{fontSize:13,color:C.muted}}>/{billingCycle==="yearly"?(isRTL?"سنة":"yr"):(isRTL?"شهر":"mo")}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Savings callout (annual only) + no-credit-card hint */}
                  <div style={{minHeight:20,marginBottom:12}}>
                    {billingCycle==="yearly"&&(pkg.priceMonthly||0)>0&&(pkg.priceYearly||0)>0&&(pkg.priceMonthly*12-pkg.priceYearly)>0&&(
                      <div style={{fontSize:11,color:"#2D7D52",fontWeight:600}}>
                        {isRTL?`وفّر ${pkg.priceMonthly*12-pkg.priceYearly} ${sym} مقارنة بالشهري`:`Save ${["USD","EUR","GBP"].includes(curr)?sym:""}${pkg.priceMonthly*12-pkg.priceYearly}${!["USD","EUR","GBP"].includes(curr)?" "+sym:""} vs monthly`}
                      </div>
                    )}
                    {(platform.globalTrialDays||0)>0&&hasPricing&&priceVal>0&&(
                      <div style={{fontSize:11,color:"var(--accent-a)",fontWeight:600,marginTop:2}}>
                        {isRTL?"لا حاجة لبيانات الدفع · ألغِ في أي وقت":"No credit card required · Cancel anytime"}
                      </div>
                    )}
                  </div>
                  <div style={{height:1,background:theme==="dark"?"rgba(255,255,255,.08)":"#E8E5E0",marginBottom:16}}/>
                  {/* Modules — incremental display */}
                  <div style={{flex:1,marginBottom:16,position:"relative",...(isLocked?{opacity:.4,pointerEvents:"none"}:{})}}>
                    {isLocked&&(
                      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:2}}>
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke={C.muted} strokeWidth="1.5"><rect x="3.5" y="7" width="9" height="7" rx="1.5"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2"/></svg>
                      </div>
                    )}
                    {prevPkg&&(
                      <div style={{fontSize:12,fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                        <span className="grad-text">&#10003;</span> <span className="grad-text">{isRTL?`كل ما في ${prevPkg.name}`:`Everything in ${prevPkg.name}`}</span>
                      </div>
                    )}
                    {!prevPkg&&moduleList.length>0&&(
                      <div style={{fontSize:11,fontWeight:600,color:C.subtle,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>{isRTL?"الوحدات الأساسية":"Core Modules"}</div>
                    )}
                    {(prevPkg?additionalModules:moduleList).map(m=>(
                      <div key={m} style={{fontSize:13,color:C.muted,padding:"3px 0",display:"flex",alignItems:"center",gap:8}}>
                        <span className="grad-text" style={{fontSize:13,fontWeight:700}}>&#10003;</span>{mn[m]||m}
                      </div>
                    ))}
                    {prevPkg&&additionalModules.length===0&&(
                      <div style={{fontSize:12,color:C.subtle,fontStyle:"italic"}}>{isRTL?"نفس الوحدات":"Same modules as "+prevPkg.name}</div>
                    )}
                  </div>
                  {/* CTA */}
                  {pkg.inviteOnly?(
                    <div style={{width:"100%",padding:"12px 0",borderRadius:10,border:`1px solid ${theme==="dark"?"rgba(255,255,255,.08)":"#E8E5E0"}`,background:"transparent",textAlign:"center",fontSize:13,color:C.subtle,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3.5" y="7" width="9" height="7" rx="1.5"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2"/></svg>
                      {isRTL?"بالدعوة فقط":"By Invitation Only"}
                    </div>
                  ):(
                    <button onClick={()=>onSignup(pkg.id)} style={{width:"100%",padding:"12px 0",borderRadius:10,border:pkg.highlight?"none":`1px solid ${theme==="dark"?"rgba(255,255,255,.15)":"#E8E5E0"}`,background:pkg.highlight?"var(--accent-grad)":"transparent",color:pkg.highlight?"#fff":C.text,fontSize:14,fontWeight:600,cursor:"pointer",boxShadow:pkg.highlight?"0 4px 16px -4px color-mix(in srgb, var(--accent-a) 45%, transparent)":"none",transition:"transform 0.18s, box-shadow 0.18s"}}>
                      {isRTL?"ابدأ تجربة مجانية":"Start Free Trial"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Final CTA ── */}
      {tog.finalCta&&(
        <section style={{maxWidth:700,margin:"0 auto",padding:"80px 24px 60px",textAlign:"center",position:"relative",zIndex:1}}>
          <h2 style={{fontFamily:hdFont,fontSize:32,fontWeight:hdWeight,fontStyle:hdStyle,color:C.text,marginBottom:14}}>
            {platform.platformFinalCtaHeading||(isRTL?<>تحكم <span className="grad-text">بمنزلك</span></>:<>Take <span className="grad-text">Control</span> of Your Household</>)}
          </h2>
          <p style={{fontSize:15,color:C.muted,marginBottom:32}}>
            {platform.platformFinalCtaSub||(isRTL?"انضم لمئات الأسر التي تستخدم كومند لتبسيط حياتهم.":"Join hundreds of households already using Command to simplify their lives.")}
          </p>
          <button className={tc(theme,"p-btn-accent-dark")} style={{padding:"14px 36px",fontSize:15,borderRadius:10}} onClick={onSignup}>
            {platform.platformFinalCtaBtn||(isRTL?"ابدأ تجربة مجانية":"Start Free Trial")}
          </button>
        </section>
      )}

      {/* ── Video popup ── */}
      {showVideo&&(
        <div onClick={()=>setShowVideo(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24,cursor:"pointer"}}>
          <div onClick={e=>e.stopPropagation()} style={{maxWidth:800,width:"100%",borderRadius:16,overflow:"hidden",background:"#000",position:"relative",cursor:"default"}}>
            <button onClick={()=>setShowVideo(false)} style={{position:"absolute",top:12,right:12,background:"rgba(255,255,255,.15)",border:"none",color:"white",width:36,height:36,borderRadius:"50%",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",zIndex:1}}>x</button>
            {platform.platformDemoVideo?(
              <video src={platform.platformDemoVideo} controls autoPlay style={{width:"100%",display:"block"}}/>
            ):(
              <div style={{padding:"80px 40px",textAlign:"center"}}>
                <div style={{fontSize:48,marginBottom:16,opacity:.4}}>&#9654;</div>
                <div style={{fontSize:18,color:"rgba(255,255,255,.6)",marginBottom:8}}>{t.demoComingSoon}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,.3)"}}>{t.demoNotUploaded}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Click-outside handler for modules popup ── */}
      {showModules&&<div onClick={()=>setShowModules(null)} style={{position:"fixed",inset:0,zIndex:5}}/>}

      {/* ── Footer ── */}
      <footer style={{padding:"24px 24px",display:"flex",justifyContent:"center",gap:16,flexWrap:"wrap",position:"relative",zIndex:1,borderTop:`1px solid ${theme==="dark"?"rgba(255,255,255,.06)":"#E8E5E0"}`}}>
        {[
          {k:"privacyPolicy",en:"Privacy Policy",ar:"سياسة الخصوصية"},
          {k:"termsOfService",en:"Terms of Service",ar:"شروط الخدمة"},
          {k:"userDataDeletion",en:"Data Deletion",ar:"حذف البيانات"},
          {k:"supportPage",en:"Support",ar:"الدعم"},
        ].map(p=>(
          <button key={p.k} onClick={()=>onLegal&&onLegal(p.k)}
            style={{background:"none",border:"none",color:C.footerText,fontSize:11,cursor:"pointer",padding:0,textDecoration:"underline",textUnderlineOffset:3}}>{isRTL?p.ar:p.en}</button>
        ))}
        <span style={{color:C.subtle,fontSize:11}}>© {new Date().getFullYear()} {(platform.legalPages||{}).companyName||"Command Platform"}</span>
      </footer>
    </div>
  );
}
